import { ScriptInjectionService } from '@/services/ScriptInjectionService';
import { INTEGRATION_KEYS, CUSTOMIZATION_KEYS } from '@/@core/constants/integrations';

/**
 * Extracts analytics configuration from integrations array
 * 
 * @param {Array} integrations - Array of integration objects
 * @returns {Object} Analytics configuration with tagManagerKey, analyticsId, and hotjarID
 */
function getAnalyticsConfig(integrations) {
  // console.log('[Analytics] getAnalyticsConfig called with:', integrations);
  const config = {
    tagManagerKey: null,
    analyticsId: null,
    hotjarID: null,
  };

  if (!integrations || integrations.length === 0) {
    // console.log('[Analytics] No integrations provided or empty array');
    return config;
  }

  // console.log('[Analytics] Processing', integrations.length, 'integrations');
  // console.log('[Analytics] Looking for keys:', {
  //   GTM: INTEGRATION_KEYS.GOOGLE_TAG_MANAGER,
  //   GA: INTEGRATION_KEYS.GOOGLE_ANALYTICS,
  //   HOTJAR: INTEGRATION_KEYS.HOTJAR,
  // });
  // console.log('[Analytics] Customization keys:', CUSTOMIZATION_KEYS);

  // Single pass through integrations array for better performance
  integrations.forEach((integration, index) => {
    // console.log(`[Analytics] Processing integration ${index}:`, {
    //   key: integration?.key,
    //   hasCustomizations: !!integration?.customizations,
    //   customizations: integration?.customizations,
    // });

    if (!integration || !integration.key) {
      // console.log(`[Analytics] Integration ${index} skipped: missing key`);
      return;
    }

    switch (integration.key) {
      case INTEGRATION_KEYS.GOOGLE_TAG_MANAGER:
        config.tagManagerKey =
          integration.customizations?.[CUSTOMIZATION_KEYS.GTM_KEY] || null;
        // console.log(`[Analytics] Found GTM key:`, config.tagManagerKey);
        break;
      case INTEGRATION_KEYS.GOOGLE_ANALYTICS:
        config.analyticsId =
          integration.customizations?.[CUSTOMIZATION_KEYS.GA_MEASUREMENT_ID] || null;
        // console.log(`[Analytics] Found GA ID:`, config.analyticsId);
        break;
      case INTEGRATION_KEYS.HOTJAR:
        config.hotjarID =
          integration.customizations?.[CUSTOMIZATION_KEYS.HOTJAR_SITE_ID] || null;
        // console.log(`[Analytics] Found Hotjar ID:`, config.hotjarID);
        break;
      default:
        // console.log(`[Analytics] Integration ${index} key "${integration.key}" not matched`);
        break;
    }
  });

  // console.log('[Analytics] Final config:', config);
  return config;
}

/**
 * Composable for handling community analytics injection
 */
export function useCommunityAnalytics() {
  // console.log('useCommunityAnalytics...');
  /**
   * Injects analytics scripts based on integrations configuration
   * 
   * @param {Array} integrations - Array of integration objects
   */
  const injectAnalytics = (integrations) => {
    // console.log('[Analytics] injectAnalytics called with integrations:', integrations);
    try {
      const config = getAnalyticsConfig(integrations);
      // console.log('[Analytics] Extracted config:', config);

      // Google Tag Manager: The Main community tag must be in the entire hierarchy
      if (config.tagManagerKey) {
        // console.log(
        //   '[Analytics] Injecting Google Tag Manager with Key:',
        //   config.tagManagerKey,
        // );
        ScriptInjectionService.injectGoogleTagManager(config.tagManagerKey);
        ScriptInjectionService.injectGoogleTagManagerNoscript(config.tagManagerKey);
      } else {
        // console.log('[Analytics] No Google Tag Manager key found');
      }

      // Google Analytics: The Main community tag must be in the entire hierarchy
      if (config.analyticsId) {
        // console.log(
        //   '[Analytics] Injecting Google Analytics with Measurement ID:',
        //   config.analyticsId,
        // );
        ScriptInjectionService.injectGoogleAnalytics(config.analyticsId);
      } else {
        // console.log('[Analytics] No Google Analytics ID found');
      }

      // Hotjar
      if (config.hotjarID) {
        // console.log('[Analytics] Injecting Hotjar with Site ID:', config.hotjarID);
        ScriptInjectionService.injectHotjar(config.hotjarID);
      } else {
        // console.log('[Analytics] No Hotjar ID found');
      }
    } catch (error) {
      console.error('[Analytics] Error injecting analytics scripts:', error);
      // Don't throw - analytics failures shouldn't break the app
    }
  };

  return {
    injectAnalytics,
    getAnalyticsConfig, // Exported for testing purposes
  };
}
